import axiosClient from '@/api/axiosClient'
import areaApi from '@/api/config/areaApi'
import soilDataConfigApi from '@/api/config/soilDataConfigApi'
import emitterApi from '@/api/operations/emitterApi'
import plantApi from '@/api/operations/plantApi'
import { ExtendedStationDetail } from '@/store/slices/DiagnosticMapView/types'
import { RootState } from '@/store/stores'
import { AreaType } from '@/types/areaZone'
import { DATA_PAGINATION, SearchParams } from '@/types/common'
import { Emitter } from '@/types/emitter'
import { Plant } from '@/types/plant'
import { Soil } from '@/types/soil'
import { SearchStation, SetupStation } from '@/types/station'
import {
  LocationFiltered,
} from '@/types/program'
import { queryStringify } from '@/ultils'
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'
import { fetchStationMapOverlay } from '@store/slices/DiagnosticMapView/diagnosticMapViewSlice'
import _ from 'lodash'

type PlantWithShow = Plant & { isShow: boolean }
type EmitterWithShow = Emitter & { isShow: boolean }
type SoilWithShow = Soil & { isShow: boolean }

export const fetchAreaSub = createAsyncThunk(
  'stationGeoTagDevice/fetchAreaSub',
  async (interfaceId: number) => {
    return await areaApi
      .getAll({ interfaceId, PageSize: 0 })
      .then((res) => res.data.data)
      .catch(() => [] as AreaType[])
  },
)

export const fetchPlants = createAsyncThunk(
  'stationGeoTagDevice/fetchPlants',
  async (interfaceId: number) => {
    return await plantApi
      .getAll({ interfaceId, PageSize: 0 })
      .then((res) => res.data.data || [])
      .catch(() => [] as Plant[])
  },
)

export const fetchEmitters = createAsyncThunk(
  'stationGeoTagDevice/fetchEmitters',
  async (interfaceId: number) => {
    return await emitterApi
      .getAll({ interfaceId, PageSize: 0 })
      .then((res) => res.data.data || [])
      .catch(() => [] as Emitter[])
  },
)

export const fetchSoils = createAsyncThunk(
  'stationGeoTagDevice/fetchSoils',
  async (interfaceId: number) => {
    return await soilDataConfigApi
      .getAll({ interfaceId, PageSize: 0 })
      .then((res) => res.data.data || [])
      .catch(() => [] as Soil[])
  },
)

export const fetchAllFilterData = createAsyncThunk(
  'stationGeoTagDevice/fetchAllFilterData',
  async (interfaceId: number, { dispatch }) => {

    const t = await Promise.all([
      dispatch(fetchAreaSub(interfaceId)),
      dispatch(fetchPlants(interfaceId)),
      dispatch(fetchEmitters(interfaceId)),
      dispatch(fetchSoils(interfaceId)),
    ])
    return t
  },
)

export const fetchStationData = createAsyncThunk(
  'stationGeoTagDevice/fetchStationData',
  async (param: SearchStation) => {
    try {

      const { PageSize, interfaceId, Keyword, ...rest } = param || {}

      const url = `/api/station/${interfaceId}/getlistwithdetail${queryStringify(rest)}`

      const res = await axiosClient.get<DATA_PAGINATION<SetupStation>>(url, {
        params: (_.omitBy({
          PageSize, interfaceId, Keyword,
        }, value => _.isNil(value) || value === '')),
      })

      return res.data.data || []
    } catch (error) {
      throw error
    }
  },
)

export const fetchStationProgramData = createAsyncThunk(
  'stationGeoTagDevice/fetchStationProgramData',
  async (param: SearchStation) => {
    try {

      const { PageSize, interfaceId, Keyword, ...rest } = param || {}

      const url = `/api/station/program-station/${interfaceId}/${queryStringify(rest)}`

      const res = await axiosClient.get<DATA_PAGINATION<LocationFiltered>>(url, {
        params: (_.omitBy({
          PageSize, interfaceId, Keyword,
        }, value => _.isNil(value) || value === '')),
      })

      return res.data.data || []
    } catch (error) {
      throw error
    }
  },
)

export const fetchSoilMoistureData = createAsyncThunk(
  'stationGeoTagDevice/fetchSoilMoistureData',
  async ({ interfaceId, paramSoil }: {
    interfaceId: number
    paramSoil?: SearchParams
  }) => {
    try {
      const url = `/api/sensor/${interfaceId}/mapoverlay`
      const res = await axiosClient.get<SetupStation>(url, { params: paramSoil })
      const data: SetupStation[] = Array.isArray(res.data) ? res.data : []
      return data
    } catch (error) {
      throw error
    }
  },
)

export const fetchFlowSensorData = createAsyncThunk(
  'stationGeoTagDevice/fetchFlowSensorData',
  async ({ interfaceId, paramSoil }: {
    interfaceId: number
    paramSoil?: SearchParams
  }) => {
    try {
      const url = `/api/flowsensor/${interfaceId}/mapoverlay`
      const res = await axiosClient.get<SetupStation>(url, { params: paramSoil })
      const data: SetupStation[] = Array.isArray(res.data) ? res.data : []
      return data
    } catch (error) {
      throw error
    }
  },
)

// https://api-staging.cisgenics.com/api/station/421758/getlistwithdetail?EmitterIds=1&PlantIds=1&SoilIds=1&SubAreaIds=2&SubAreaIds=3&PageSize=0&interfaceId=421758
export enum EStationTab {
  StationData = 0,
  SoilMoisture = 1,
  FlowSensor = 2,
}

const initialState = {
  loading: false,
  tab: 0 as EStationTab,
  stations: [] as ExtendedStationDetail[],
  stationsFiltered: [] as ExtendedStationDetail[],

  //
  stationData: [] as SetupStation[],
  soilMoisture: [] as SetupStation[],
  flowSensor: [] as SetupStation[],
  filter: {
    Keyword: '',
    EmitterIds: [] as number[],
    PlantIds: [] as number[],
    SubAreaIds: [] as number[],
    SoilIds: [] as number[],
  },
  rawFilter: {
    SubAreaIds: [] as any[],
    PlantIds: [] as any[],
    EmitterIds: [] as any[],
    SoilIds: [] as any[],
  },
  areas: [] as AreaType[],
  plants: [] as PlantWithShow[],
  emitters: [] as EmitterWithShow[],
  soils: [] as SoilWithShow[],
  filterDataLoading: {
    areas: false,
    plants: false,
    emitters: false,
    soils: false,
  },
  filterFlowSensor: {
    Keyword: '',
  },
  filterSoilMoisture: {
    Keyword: '',
  },
  stationDataLoading: false,
  soilMoistureLoading: false,
  flowSensorLoading: false,
}

export enum EFilterType {
  SubAreaIds = 'SubAreaIds',
  LocationIds = 'LocationIds',
  PlantIds = 'PlantIds',
  EmitterIds = 'EmitterIds',
  SoilIds = 'SoilIds',
}

export const stationGeoTagDeviceSlice = createSlice({
  name: 'stationGeoTagDevice',
  initialState,
  reducers: {
    // areas and subs
    // plants, emitter, soil
    reset: (state) => {
      state.rawFilter = initialState.rawFilter
      state.filter = initialState.filter
    },
    changeTab: (state, action: PayloadAction<{ value: EStationTab }>) => {
      const { value } = action.payload
      if (state.tab !== value) state.tab = value
    },
    changeKeyword: (state, action) => {
      const { value } = action.payload
      state.filter.Keyword = value
    },

    changeKeywordForTab: (state, action: PayloadAction<{ tab: EStationTab, value: string }>) => {
      const { tab, value } = action.payload;
      switch (tab) {
        case EStationTab.StationData:
          state.filter.Keyword = value;
          break;
        case EStationTab.FlowSensor:
          state.filterFlowSensor.Keyword = value;
          break;
        case EStationTab.SoilMoisture:
          state.filterSoilMoisture.Keyword = value;
          break;
      }
    },

    setSubAreaFilterRaw: (state, action) => {
      const { value } = action.payload

      const allSubAreas = value.map((area: { subAreas: any }) => area.subAreas).flat()
      const selectedSubAreas = allSubAreas.filter((subArea: { isSelected: any }) => subArea.isSelected)
      const selectedSubAreaIds = selectedSubAreas.map((subArea: { id: any }) => subArea.id)

      state.rawFilter.SubAreaIds = selectedSubAreas
      state.filter.SubAreaIds = selectedSubAreaIds
      /**
       * @todo:
       * backend not return plantId, emitterId, soilId in APi response, so can not self filtering
       * api api/station/:interfaceId/getlistwithdetail?EmitterIds=1&PlantIds=1&SoilIds=1&SubAreaIds=2&SubAreaIds=3&SubAreaIds=5&SubAreaIds=6&SubAreaIds=334&PageSize=0&interfaceId=421758
       */
      // state.stationsFiltered = state.stations.filter((station: any) =>
      //   selectedSubAreaIds.includes(station.subAreaId),
      // )
    },
    setPlantEmitterSoilFilterRaw: (state, action) => {
      const { value } = action.payload

      // {
      //   "PlantIds": [
      //   {
      //     "id": 1,
      //     "name": "Plant 1"
      //   },
      //   {
      //     "id": 2,
      //     "name": "Shrub 2"
      //   }
      // ],
      //   "EmitterIds": [
      //   {
      //     "id": 1,
      //     "name": "Emitter 1"
      //   }
      // ],
      //   "SoilIds": []
      // }
      if (value) {
        const { PlantIds, EmitterIds, SoilIds } = value
        state.rawFilter.PlantIds = PlantIds
        state.rawFilter.EmitterIds = EmitterIds
        state.rawFilter.SoilIds = SoilIds

        state.filter.PlantIds = PlantIds.map((plant: { id: any }) => plant.id)
        state.filter.EmitterIds = EmitterIds.map((emitter: { id: any }) => emitter.id)
        state.filter.SoilIds = SoilIds.map((soil: { id: any }) => soil.id)
      }

    },
    setPlantEmitterSoilIDFilter: (state, action) => {
      const { value } = action.payload
      if (value) {
        const { PlantIds, EmitterIds, SoilIds } = value
        state.filter.PlantIds = PlantIds
        state.filter.EmitterIds = EmitterIds
        state.filter.SoilIds = SoilIds
      }
    },
    setRawFilter: (state, action) => {
      const { value } = action.payload

      if (value) {
        const { SubAreaIds, PlantIds, EmitterIds, SoilIds } = value

        state.rawFilter.SubAreaIds = PlantIds
        state.rawFilter.PlantIds = PlantIds
        state.rawFilter.EmitterIds = EmitterIds
        state.rawFilter.SoilIds = SoilIds

        state.filter.SubAreaIds = SubAreaIds.map((subArea: { id: any }) => subArea.id)
        state.filter.PlantIds = PlantIds.map((plant: { id: any }) => plant.id)
        state.filter.EmitterIds = EmitterIds.map((emitter: { id: any }) => emitter.id)
        state.filter.SoilIds = SoilIds.map((soil: { id: any }) => soil.id)
      }
    },
    removeSingleById: (state, action) => {
      const { type, value } = action.payload

      if (value) {
        switch (type) {
          case EFilterType.SubAreaIds:
            state.filter.SubAreaIds = state.filter.SubAreaIds.filter((id) => id !== value,
            )
            state.rawFilter.SubAreaIds = state.rawFilter.SubAreaIds.filter(
              (item: any) => item.id !== value,
            )
            break
          case EFilterType.PlantIds:
            state.filter.PlantIds = state.filter.PlantIds.filter(
              (id) => id !== value,
            )
            state.rawFilter.PlantIds = state.rawFilter.PlantIds.filter(
              (item: any) => item.id !== value,
            )
            break
          case EFilterType.EmitterIds:
            state.filter.EmitterIds = state.filter.EmitterIds.filter(
              (id) => id !== value,
            )
            state.rawFilter.EmitterIds = state.rawFilter.EmitterIds.filter(
              (item: any) => item.id !== value,
            )
            break
          case EFilterType.SoilIds:
            state.filter.SoilIds = state.filter.SoilIds.filter(
              (id) => id !== value,
            )
            state.rawFilter.SoilIds = state.rawFilter.SoilIds.filter(
              (item: any) => item.id !== value,
            )
            break
        }

      }
    },
    resetSubAreaFilter: (state, action) => {
      state.rawFilter.SubAreaIds = []
      state.filter.SubAreaIds = []
    },

    changeArea: (state, action) => {
      const { value: areaId } = action.payload

      state.areas = state.areas.map((area) => {

        if (area.id === areaId) {
          const isSelected = !area.isSelected
          area.isSelected = isSelected
          area.subAreas.map((subArea) => {
            subArea.isSelected = isSelected
          })
        }
        return {
          ...area,
        }
      })
    },

    changeAreaSub: (state, action) => {
      const { value: { areaId, subAreaId } } = action.payload

      state.areas = state.areas.map((area) => {
        if (area.id === areaId) {
          const newSubAreas = area.subAreas?.map(sub => ({
            ...sub,
            isSelected: sub.id === subAreaId ? !sub.isSelected : sub.isSelected,
          }))

          const allSubAreasSelected = newSubAreas?.every(sub => sub.isSelected)
          const someSubAreasSelected = newSubAreas?.some(sub => sub.isSelected)

          return {
            ...area,
            isSelected: allSubAreasSelected,
            isIndeterminate: someSubAreasSelected && !allSubAreasSelected,
            subAreas: newSubAreas,
          }
        }
        return area
      })
    },

    changeSinglePlantEmitterSoil: (
      state,
      action: PayloadAction<{ type: EFilterType, value: { id: number, [key: string]: any } }>) => {

      const { type, value } = action.payload

      const { id } = value

      let isChecked: boolean

      switch (type) {
        case EFilterType.PlantIds:
          isChecked = _.map(state.rawFilter.PlantIds, 'id').includes(id)

          if (!isChecked) {
            if (!_.find(state.rawFilter.PlantIds, (item: any) => item.id === id)) {
              state.rawFilter.PlantIds.push(value)
            }
          } else {
            state.rawFilter.PlantIds = state.rawFilter.PlantIds.filter(
              (item: any) => item.id !== id,
            )
            // state.filter.PlantIds = state.filter.PlantIds.filter((item) => item !== id,)
          }
          break
        case EFilterType.EmitterIds:
          isChecked = _.map(state.rawFilter.EmitterIds, 'id').includes(id)

          if (!isChecked) {
            if (!_.find(state.rawFilter.EmitterIds, (item: any) => item.id === id)) {
              state.rawFilter.EmitterIds.push(value)
            }
            // state.filter.EmitterIds.push(id)
          } else {
            state.rawFilter.EmitterIds = state.rawFilter.EmitterIds.filter(
              (item: any) => item.id !== id,
            )
            // state.filter.EmitterIds = state.filter.EmitterIds.filter((item) => item !== id,)
          }
          break
        case EFilterType.SoilIds:
          isChecked = _.map(state.rawFilter.SoilIds, 'id').includes(id)

          if (!isChecked) {
            if (!_.find(state.rawFilter.SoilIds, (item: any) => item.id === id)) {
              state.rawFilter.SoilIds.push(value)
            }
            // state.filter.SoilIds.push(id)
          } else {
            state.rawFilter.SoilIds = state.rawFilter.SoilIds.filter(
              (item: any) => item.id !== id,
            )
            // state.filter.SoilIds = state.filter.SoilIds.filter((item) => item !== id,)
          }
          break
      }

    },

    updateSinglePlantEmitterSoil: (state, action: PayloadAction<{ type: EFilterType }>) => {
      /**
       * Wait user press DONE on filter to update filter.
       */
      const { type } = action.payload

      switch (type) {
        case EFilterType.PlantIds:
          state.filter.PlantIds = state.rawFilter.PlantIds.map((plant: { id: any }) => plant.id)
          break
        case EFilterType.EmitterIds:
          state.filter.EmitterIds = state.rawFilter.EmitterIds.map((emitter: { id: any }) => emitter.id)
          break
        case EFilterType.SoilIds:
          state.filter.SoilIds = state.rawFilter.SoilIds.map((soil: { id: any }) => soil.id)
          break
      }
    },

    searchSinglePlantEmitterSoil: (
      state,
      action: PayloadAction<{ type: EFilterType, value: string | '' | null }>) => {

      const { type, value } = action.payload

      switch (type) {
        case EFilterType.PlantIds:
          state.plants = state.plants.map(plant => ({
            ...plant,
            isShow: value ? plant.description.toLowerCase().includes(value.toLowerCase()) : true,
          }))
          break
        case EFilterType.EmitterIds:
          state.emitters = state.emitters.map(emitter => ({
            ...emitter,
            isShow: value ? emitter.emitter.toLowerCase().includes(value.toLowerCase()) : true,
          }))
          break
        case EFilterType.SoilIds:
          state.soils = state.soils.map(soil => ({
            ...soil,
            isShow: value ? soil.description.toLowerCase().includes(value.toLowerCase()) : true,
          }))
          break
      }

    },


    resetArea: (state) => {

      state.areas = state.areas.map(area => ({
        ...area,
        isSelected: false,
        isIndeterminate: false,
        subAreas: area.subAreas?.map(sub => ({
          ...sub,
          isSelected: false,
        })),
      }))

    },

  },
  extraReducers: (builder) => {
    builder
      // fetch stationMapOverlay
      .addCase(fetchStationMapOverlay.pending, (state, action) => {
        state.loading = true
      })
      .addCase(fetchStationMapOverlay.rejected, (state, action) => {
        state.loading = false
      })
      .addCase(fetchStationMapOverlay.fulfilled, (state, action) => {
        state.loading = false
        state.stations = action.payload
      })
      // fetch areas
      .addCase(fetchAreaSub.pending, (state) => {
        state.filterDataLoading.areas = true
      })
      .addCase(fetchAreaSub.rejected, (state) => {
        state.filterDataLoading.areas = false
      })
      .addCase(fetchAreaSub.fulfilled, (state, action) => {
        state.filterDataLoading.areas = false
        state.areas = action.payload
      })
      // fetch plants
      .addCase(fetchPlants.pending, (state) => {
        state.filterDataLoading.plants = true
      })
      .addCase(fetchPlants.rejected, (state) => {
        state.filterDataLoading.plants = false
      })
      .addCase(fetchPlants.fulfilled, (state, action) => {
        state.filterDataLoading.plants = false
        state.plants = action.payload.map(plant => ({ ...plant, isShow: true }))
      })
      // fetch emitters
      .addCase(fetchEmitters.pending, (state) => {
        state.filterDataLoading.emitters = true
      })
      .addCase(fetchEmitters.rejected, (state) => {
        state.filterDataLoading.emitters = false
      })
      .addCase(fetchEmitters.fulfilled, (state, action) => {
        state.filterDataLoading.emitters = false
        state.emitters = action.payload.map(emitter => ({ ...emitter, isShow: true }))
      })
      // fetch soils
      .addCase(fetchSoils.pending, (state) => {
        state.filterDataLoading.soils = true
      })
      .addCase(fetchSoils.rejected, (state) => {
        state.filterDataLoading.soils = false
      })
      .addCase(fetchSoils.fulfilled, (state, action) => {
        state.filterDataLoading.soils = false
        state.soils = action.payload.map((soil: any) => ({ ...soil, isShow: true }))
      })
      // fetch station data
      .addCase(fetchStationData.pending, (state) => {
        state.stationDataLoading = true
      })
      .addCase(fetchStationData.rejected, (state) => {
        state.stationDataLoading = false
      })
      .addCase(fetchStationData.fulfilled, (state, action) => {
        state.stationDataLoading = false
        state.stationData = action.payload
      })
      // fetch soil moisture data
      .addCase(fetchSoilMoistureData.pending, (state) => {
        state.soilMoistureLoading = true
      })
      .addCase(fetchSoilMoistureData.rejected, (state) => {
        state.soilMoistureLoading = false
      })
      .addCase(fetchSoilMoistureData.fulfilled, (state, action) => {
        state.soilMoistureLoading = false
        state.soilMoisture = action.payload
      })
      // fetch flow sensor data
      .addCase(fetchFlowSensorData.pending, (state) => {
        state.flowSensorLoading = true
      })
      .addCase(fetchFlowSensorData.rejected, (state) => {
        state.flowSensorLoading = false
      })
      .addCase(fetchFlowSensorData.fulfilled, (state, action) => {
        state.flowSensorLoading = false
        state.flowSensor = action.payload
      })
  },
})

export const stationGeoTagDeviceSliceActions = stationGeoTagDeviceSlice.actions

export const stationGeoTagDeviceState = (state: RootState) =>
  state.stationGeoTagDevice

export default stationGeoTagDeviceSlice.reducer
